import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Typography,
  Space,
  message,
  Divider,
  Row,
  Col,
  Slider,
  Select,
  Alert,
  Tag,
} from 'antd';
import { SaveOutlined, CrownOutlined, BulbOutlined, ReloadOutlined } from '@ant-design/icons';
import { strategiesAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const VIPStrategyConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [vipStrategy, setVipStrategy] = useState(null);
  const [normalStrategy, setNormalStrategy] = useState(null);

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      // 获取所有策略
      const response = await strategiesAPI.getAll({ limit: 100 });
      if (response.data.success) {
        const strategies = response.data.data.strategies;
        
        // 找到最新的 VIP 每日策略
        const vip = strategies.find(s => s.type === 'daily' && s.isVip && s.isPublished);
        // 找到最新的普通每日策略
        const normal = strategies.find(s => s.type === 'daily' && !s.isVip && s.isPublished);
        
        setVipStrategy(vip);
        setNormalStrategy(normal);
        
        if (vip) {
          // Parse focusAreas if it's a string
          const focusAreas = typeof vip.focusAreas === 'string' 
            ? JSON.parse(vip.focusAreas || '[]') 
            : (vip.focusAreas || []);
          
          form.setFieldsValue({
            ...vip,
            focusAreas,
          });
        } else {
          // 设置默认值
          form.setFieldsValue({
            title: 'VIP Exclusive Daily Strategy',
            type: 'daily',
            marketAnalysis: '',
            focusAreas: [],
            riskWarning: 'This is VIP exclusive content. Trade with caution and manage your risk properly.',
            riskLevel: 5,
            isVip: true,
            isPublished: true,
            status: 'active',
          });
        }
      }
    } catch (error) {
      message.error('获取策略配置失败');
      console.error('Fetch strategies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      // 确保是 VIP 策略
      values.isVip = true;
      values.type = 'daily';
      values.isPublished = true;
      values.publishedAt = new Date().toISOString();
      
      if (vipStrategy) {
        // 更新现有策略
        await strategiesAPI.update(vipStrategy.id, values);
        message.success('VIP策略更新成功');
      } else {
        // 创建新策略
        await strategiesAPI.create(values);
        message.success('VIP策略创建成功');
      }
      
      fetchStrategies();
    } catch (error) {
      message.error('保存失败');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const copyFromNormal = () => {
    if (normalStrategy) {
      const focusAreas = typeof normalStrategy.focusAreas === 'string' 
        ? JSON.parse(normalStrategy.focusAreas || '[]') 
        : (normalStrategy.focusAreas || []);
      
      form.setFieldsValue({
        title: normalStrategy.title + ' (VIP Enhanced)',
        marketAnalysis: normalStrategy.marketAnalysis,
        focusAreas,
        riskWarning: normalStrategy.riskWarning,
        riskLevel: normalStrategy.riskLevel,
      });
      message.success('已从普通策略复制内容，请修改后保存');
    } else {
      message.warning('没有可复制的普通策略');
    }
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>
          <CrownOutlined style={{ marginRight: 8, color: '#faad14' }} />
          VIP 专属策略配置
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={copyFromNormal}>
            从普通策略复制
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSave}
            loading={saving}
          >
            保存策略
          </Button>
        </Space>
      </div>

      <Alert
        message="VIP 策略说明"
        description="此处配置的策略将只对 VIP 用户显示在首页的 Daily Market Strategy 区域。普通用户和游客将看到普通策略内容。"
        type="info"
        showIcon
        icon={<CrownOutlined />}
      />

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <BulbOutlined style={{ color: '#faad14' }} />
                VIP 策略内容
                <Tag color="gold">VIP专属</Tag>
              </Space>
            }
            loading={loading}
          >
            <Form form={form} layout="vertical">
              <Form.Item 
                name="title" 
                label="策略标题" 
                rules={[{ required: true, message: '请输入标题' }]}
              >
                <Input placeholder="VIP Exclusive Daily Strategy" />
              </Form.Item>

              <Form.Item 
                name="marketAnalysis" 
                label="市场分析 (VIP独家内容)" 
                rules={[{ required: true, message: '请输入市场分析' }]}
                extra="此内容仅VIP用户可见，可包含更详细的分析和交易建议"
              >
                <TextArea 
                  rows={6} 
                  placeholder="为VIP用户提供更深入的市场分析、具体的交易建议、目标价位等..." 
                />
              </Form.Item>

              <Form.Item 
                name="focusAreas" 
                label="重点关注领域"
                extra="可输入自定义标签"
              >
                <Select mode="tags" placeholder="输入关注领域">
                  <Option value="AAPL">AAPL 苹果</Option>
                  <Option value="NVDA">NVDA 英伟达</Option>
                  <Option value="TSLA">TSLA 特斯拉</Option>
                  <Option value="MSFT">MSFT 微软</Option>
                  <Option value="AI Stocks">AI股票</Option>
                  <Option value="Technology">科技板块</Option>
                  <Option value="Semiconductor">半导体</Option>
                  <Option value="Cloud Computing">云计算</Option>
                </Select>
              </Form.Item>

              <Divider>风险评估</Divider>

              <Form.Item 
                name="riskLevel" 
                label="风险等级 (1-10)"
              >
                <Slider 
                  min={1} 
                  max={10} 
                  marks={{ 
                    1: '低风险', 
                    3: '较低', 
                    5: '中等', 
                    7: '较高', 
                    10: '高风险' 
                  }} 
                />
              </Form.Item>

              <Form.Item 
                name="riskWarning" 
                label="风险提示"
              >
                <TextArea 
                  rows={2} 
                  placeholder="请输入风险提示信息..." 
                />
              </Form.Item>

              <Divider>发布设置</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="status" label="状态">
                    <Select>
                      <Option value="new">新发布</Option>
                      <Option value="active">进行中</Option>
                      <Option value="completed">已完成</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="isPublished" label="是否发布" valuePropName="checked">
                    <Switch checkedChildren="已发布" unCheckedChildren="草稿" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="当前策略状态" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">普通用户看到的策略：</Text>
                <div style={{ marginTop: 8 }}>
                  {normalStrategy ? (
                    <>
                      <Tag color="blue">已配置</Tag>
                      <Text strong>{normalStrategy.title}</Text>
                    </>
                  ) : (
                    <Tag color="red">未配置</Tag>
                  )}
                </div>
              </div>
              <Divider />
              <div>
                <Text type="secondary">VIP用户看到的策略：</Text>
                <div style={{ marginTop: 8 }}>
                  {vipStrategy ? (
                    <>
                      <Tag color="gold">已配置</Tag>
                      <Text strong>{vipStrategy.title}</Text>
                    </>
                  ) : (
                    <Tag color="orange">未配置</Tag>
                  )}
                </div>
              </div>
            </Space>
          </Card>

          <Card title="使用说明">
            <Space direction="vertical">
              <Text type="secondary">
                <strong>1.</strong> VIP策略将替换首页默认的 Daily Market Strategy 内容
              </Text>
              <Text type="secondary">
                <strong>2.</strong> 只有VIP用户和管理员可以看到VIP策略
              </Text>
              <Text type="secondary">
                <strong>3.</strong> 普通用户和游客将继续看到普通策略
              </Text>
              <Text type="secondary">
                <strong>4.</strong> 可使用"从普通策略复制"快速创建VIP版本
              </Text>
              <Text type="secondary">
                <strong>5.</strong> VIP内容可包含更详细的分析和具体交易建议
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default VIPStrategyConfig;

